import { z } from "zod";
import { optionalUnlessRequired, requiredText, type FieldDescriptor } from "./types.js";

// Composite: title, first, middle, last — configurable which parts show
// (docs/01-PRODUCT-SPEC.md §3.2). Stored as one object, flattened to four columns on export
// (§9.3 "flatten composite fields (`name` → four columns)").
const configSchema = z.object({
  showTitle: z.boolean().default(false),
  showMiddleName: z.boolean().default(true),
  titleOptions: z.array(z.string()).default(["Mr", "Mrs", "Miss", "Ms", "Dr", "Prof"]),
});

type NameConfig = z.infer<typeof configSchema>;

export const nameField: FieldDescriptor<NameConfig> = {
  type: "name",
  label: "Name",
  category: "basic_info",
  icon: "User",
  configSchema,
  valueSchema: (config, isRequired) => {
    const shape: Record<string, z.ZodTypeAny> = {
      firstName: isRequired ? requiredText() : z.string().trim().optional(),
      lastName: isRequired ? requiredText() : z.string().trim().optional(),
    };
    if (config.showTitle) shape.title = z.string().trim().optional();
    if (config.showMiddleName) shape.middleName = z.string().trim().optional();

    return optionalUnlessRequired(z.object(shape).strict(), isRequired);
  },
  defaultConfig: {
    showTitle: false,
    showMiddleName: true,
    titleOptions: ["Mr", "Mrs", "Miss", "Ms", "Dr", "Prof"],
  },
  supportsProfileBinding: true,
  profileParts: {
    title: "person.title",
    firstName: "person.first_name",
    middleName: "person.middle_name",
    lastName: "person.last_name",
  },
  defaultSearchable: true,
  renderHints: { isComposite: true },
};
